import { Lock as LockIcon, Logout as LogoutIcon, Person as PersonIcon } from "@mui/icons-material";
import {
  Box,
  Divider,
  FormControl,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TestId } from "../../constants/testIds";
import { darkThemes, lightThemes, useThemeSwitcher } from "../../contexts/ThemeSwitcherContext";
import { useDensity } from "../../contexts/DensityContext";
import type { AppDispatch } from "../../store";
import { performLogout } from "../../store/features/userSlice";
import type translations from "../../translations/translations.json";
import type { User } from "../../types/user";
import UserAvatar from "../common/UserAvatar";
import DensitySlider from "../common/DensitySlider";

type TranslationKeys = keyof typeof translations.en;

interface SidebarProfileProps {
  user: User | null;
}

const SidebarProfile: React.FC<SidebarProfileProps> = ({ user }) => {
  const userName = user?.name || "";
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { themeName, setThemeName, currentThemeInfo } = useThemeSwitcher();
  const { density, setDensityForCurrentViewport } = useDensity();

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(performLogout());
    handleProfileClose();
    navigate("/login");
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    i18n.changeLanguage(event.target.value);
  };

  const handleThemeChange = (event: SelectChangeEvent<string>) => {
    setThemeName(event.target.value as any);
  };

  const handleChangePassword = () => {
    handleProfileClose();
    navigate("/reset-password");
  };

  const handleOpenProfile = () => {
    handleProfileClose();
    navigate("/profile");
  };

  return (
    <Box data-testid={TestId.LOGIN.USER_MENU}>
      <ListItemButton
        onClick={handleProfileClick}
        data-testid={TestId.SIDEBAR.PROFILE}
        sx={{
          minHeight: 40,
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
          <UserAvatar user={user} size={28} />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  lineHeight: 1,
                  mb: 0.5,
                  fontSize: "0.65rem",
                }}
              >
                {t("Signed in as" as TranslationKeys)}
              </Typography>
              <Typography
                variant="body2"
                data-testid={TestId.SIDEBAR.PROFILE_NAME}
                sx={{
                  fontWeight: 500,
                  fontSize: "0.9rem",
                }}
              >
                {userName}
              </Typography>
            </Box>
          }
        />
      </ListItemButton>
      <Menu
        anchorEl={profileMenuAnchorEl}
        open={Boolean(profileMenuAnchorEl)}
        onClose={handleProfileClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 0,
            minWidth: 200,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              bottom: 0,
              left: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="body2">{t("Language" as TranslationKeys)}</Typography>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={i18n.language}
                onChange={handleLanguageChange}
                size="small"
                data-testid={TestId.SIDEBAR.LANGUAGE_SELECT}
                sx={{
                  fontSize: "0.875rem",
                  "& .MuiSelect-select": {
                    py: 0.5,
                    pr: 3,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: "1rem",
                  },
                }}
              >
                <MenuItem
                  value="en"
                  sx={{ fontSize: "0.875rem" }}
                  data-testid={TestId.SIDEBAR.LANGUAGE_OPTION_EN}
                >
                  {t("English")}
                </MenuItem>
                <MenuItem
                  value="id"
                  sx={{ fontSize: "0.875rem" }}
                  data-testid={TestId.SIDEBAR.LANGUAGE_OPTION_ID}
                >
                  {t("Indonesia")}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="body2">{t("Theme" as TranslationKeys)}</Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={themeName}
                onChange={handleThemeChange}
                size="small"
                data-testid={TestId.SIDEBAR.THEME_SELECT}
                sx={{
                  fontSize: "0.875rem",
                  "& .MuiSelect-select": {
                    py: 0.5,
                    pr: 3,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: "1rem",
                  },
                }}
              >
                <ListSubheader
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    backgroundColor: "background.paper",
                    "&:hover": {
                      backgroundColor: "background.paper",
                    },
                  }}
                >
                  {t("Light Themes" as TranslationKeys)}
                </ListSubheader>
                {lightThemes.map((theme) => (
                  <MenuItem
                    key={theme.name}
                    value={theme.name}
                    sx={{ fontSize: "0.875rem" }}
                    data-testid={
                      theme.name === "lightBlue" ? TestId.SIDEBAR.THEME_OPTION_LIGHT : undefined
                    }
                  >
                    {theme.label}
                  </MenuItem>
                ))}
                <ListSubheader
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    backgroundColor: "background.paper",
                    "&:hover": {
                      backgroundColor: "background.paper",
                    },
                  }}
                >
                  {t("Dark Themes" as TranslationKeys)}
                </ListSubheader>
                {darkThemes.map((theme) => (
                  <MenuItem
                    key={theme.name}
                    value={theme.name}
                    sx={{ fontSize: "0.875rem" }}
                    data-testid={
                      theme.name === "exyboxGold" ? TestId.SIDEBAR.THEME_OPTION_DARK : undefined
                    }
                  >
                    {theme.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {t("profile.density.label" as TranslationKeys)}
            </Typography>
            <DensitySlider
              variant="sidebar"
              value={density}
              onChange={setDensityForCurrentViewport}
              testId={TestId.SIDEBAR.DENSITY_SELECT}
            />
          </Box>
        </Box>
        <Divider />
        <MenuItem
          onClick={handleOpenProfile}
          sx={{
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <PersonIcon sx={{ fontSize: "1.25rem" }} />
          {t("Profile" as TranslationKeys)}
        </MenuItem>
        <MenuItem
          onClick={handleChangePassword}
          sx={{
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <LockIcon sx={{ fontSize: "1.25rem" }} />
          {t("Change Password" as TranslationKeys)}
        </MenuItem>
        <MenuItem
          onClick={handleLogout}
          data-testid={TestId.LOGIN.LOGOUT_BUTTON}
          sx={{
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "error.main",
            "&:hover": {
              backgroundColor: "error.light",
              color: "error.contrastText",
            },
          }}
        >
          <LogoutIcon sx={{ fontSize: "1.25rem" }} />
          {t("Logout" as TranslationKeys)}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SidebarProfile;
